import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  School as SchoolIcon,
  ContactPhone as EmergencyIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { api } from '../../services/api';
const getImageUrl = (path: string | null) => {
  return api.getImageUrl(path);
};

const StudentProfile: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPass, setIsChangingPass] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Profile fields (including 'id' for the user ID)
  const [profileData, setProfileData] = useState({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    studentNumber: '',
    licenseNumber: '',
    licenseExpiryDate: '',
    theoryTestPassDate: '',
    theoryTestPassReference: '',
    dvlaShareCode: '',
    eyesightTestPassed: false,
    emergencyContactName: '',
    emergencyContactPhone: '',
    medicalConditions: '',
    transmissionType: 'Manual',
    licenseFrontImagePath: '',
    licenseBackImagePath: '',
    licenseStatus: 'None',
    licenseRejectionReason: '',
    profileImageUrl: ''
  });

  // Password fields
  const [passData, setPassData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Lightbox state
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImgUrl, setLightboxImgUrl] = useState('');
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getStudentProfile();
      const data = response.data;
      setProfileData({
        id: data.id || '',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phoneNumber: data.phoneNumber || '',
        studentNumber: data.studentNumber || '',
        licenseNumber: data.licenseNumber || '',
        licenseExpiryDate: data.licenseExpiryDate ? data.licenseExpiryDate.split('T')[0] : '',
        theoryTestPassDate: data.theoryTestPassDate ? data.theoryTestPassDate.split('T')[0] : '',
        theoryTestPassReference: data.theoryTestPassReference || '',
        dvlaShareCode: data.dvlaShareCode || '',
        eyesightTestPassed: data.eyesightTestPassed ?? false,
        emergencyContactName: data.emergencyContactName || '',
        emergencyContactPhone: data.emergencyContactPhone || '',
        medicalConditions: data.medicalConditions || '',
        transmissionType: data.transmissionType || 'Manual',
        licenseFrontImagePath: data.licenseFrontImagePath || '',
        licenseBackImagePath: data.licenseBackImagePath || '',
        licenseStatus: data.licenseStatus || 'None',
        licenseRejectionReason: data.licenseRejectionReason || '',
        profileImageUrl: data.profileImageUrl || ''
      });
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      setError('An error occurred while loading profile information.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profileData.firstName || !profileData.lastName) {
      setError('First Name and Last Name fields are required.');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      await api.updateStudentProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phoneNumber: profileData.phoneNumber,
        licenseNumber: profileData.licenseNumber || null,
        licenseExpiryDate: profileData.licenseExpiryDate ? new Date(profileData.licenseExpiryDate).toISOString() : null,
        theoryTestPassDate: profileData.theoryTestPassDate ? new Date(profileData.theoryTestPassDate).toISOString() : null,
        theoryTestPassReference: profileData.theoryTestPassReference || null,
        dvlaShareCode: profileData.dvlaShareCode || null,
        eyesightTestPassed: profileData.eyesightTestPassed,
        emergencyContactName: profileData.emergencyContactName || null,
        emergencyContactPhone: profileData.emergencyContactPhone || null,
        medicalConditions: profileData.medicalConditions || null,
        transmissionType: profileData.transmissionType,
        profileImageUrl: profileData.profileImageUrl || null
      });
      setSuccessMsg('Your profile information has been successfully updated.');
      await fetchProfile(); // Refresh profile values
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError('An error occurred while updating profile.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passData.currentPassword || !passData.newPassword || !passData.confirmPassword) {
      setError('Please fill in the password fields.');
      return;
    }

    if (passData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long.');
      return;
    }

    if (passData.newPassword !== passData.confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    try {
      setIsChangingPass(true);
      setError(null);
      await api.changeSelfPassword(passData.currentPassword, passData.newPassword);
      setSuccessMsg('Your password has been successfully changed.');
      setPassData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.message || 'Your current password is incorrect or an error occurred.');
    } finally {
      setIsChangingPass(false);
    }
  };

  const handleLicenseUpload = async (e: React.ChangeEvent<HTMLInputElement>, side: 'front' | 'back') => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    try {
      setLoading(true);
      setError(null);
      // Use the user's ID GUID we retrieved from profile
      await api.uploadLicenseImage(profileData.id, side, file);
      setSuccessMsg(`Driving license ${side === 'front' ? 'front' : 'back'} side uploaded successfully.`);
      await fetchProfile();
    } catch (err: any) {
      console.error('Error uploading license image:', err);
      setError(err.response?.data?.message || 'Failed to upload driving license image.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    try {
      setLoading(true);
      setError(null);
      await api.uploadProfileImage(file);
      setSuccessMsg('Your profile picture has been successfully updated.');
      await fetchProfile();
    } catch (err: any) {
      console.error('Error uploading profile image:', err);
      setError(err.response?.data?.message || 'Failed to upload profile picture.');
    } finally {
      setLoading(false);
    }
  };

  // Lightbox Handlers
  const handleOpenImageLightbox = (url: string) => {
    setLightboxImgUrl(url);
    setRotation(0);
    setScale(1);
    setIsLightboxOpen(true);
  };

  const handleRotateRight = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 1));
  };

  if (loading && !profileData.firstName) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" fontWeight="bold" color="#1a237e" gutterBottom sx={{ mb: 3 }}>
        My Profile
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column: Profile Info */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={3}>
              <PersonIcon color="primary" fontSize="large" />
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                Personal & Driving Details
              </Typography>
            </Box>

            <Box display="flex" flexDirection="column" alignItems="center" mb={4} sx={{ position: 'relative' }}>
              <Box sx={{ position: 'relative', width: 120, height: 120, mb: 1.5 }}>
                <Avatar
                  src={profileData.profileImageUrl ? getImageUrl(profileData.profileImageUrl) : undefined}
                  sx={{ width: 120, height: 120, border: '4px solid #1a237e', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', bgcolor: 'primary.main', fontSize: '2.5rem', fontWeight: 'bold' }}
                >
                  {`${profileData.firstName?.charAt(0) || ''}${profileData.lastName?.charAt(0) || ''}`.toUpperCase() || <PersonIcon sx={{ fontSize: 60 }} />}
                </Avatar>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="profile-image-upload"
                  type="file"
                  onChange={handleProfileImageUpload}
                />
                <label htmlFor="profile-image-upload">
                  <IconButton
                    component="span"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      backgroundColor: '#1a237e',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#0d47a1' },
                      border: '2px solid #fff',
                      p: 1
                    }}
                  >
                    <UploadIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </label>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Click the button to update your profile picture (Max: 5MB)
              </Typography>
            </Box>

            <form onSubmit={handleProfileSubmit}>
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="First Name"
                    required
                    fullWidth
                    value={profileData.firstName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Last Name"
                    required
                    fullWidth
                    value={profileData.lastName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Email Address"
                    fullWidth
                    disabled
                    value={profileData.email}
                    InputProps={{
                      startAdornment: (
                        <IconButton disabled size="small" sx={{ mr: 1 }}>
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Phone Number"
                    fullWidth
                    value={profileData.phoneNumber}
                    onChange={(e) => setProfileData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <IconButton disabled size="small" sx={{ mr: 1 }}>
                          <PhoneIcon fontSize="small" />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Student Number"
                    fullWidth
                    disabled
                    value={profileData.studentNumber}
                  />
                </Grid>
 
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1.5 }}><Chip label="UK License & Exam Details" size="small" /></Divider>
                </Grid>
 
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Driving License Number"
                    fullWidth
                    value={profileData.licenseNumber}
                    onChange={(e) => setProfileData(prev => ({ ...prev, licenseNumber: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Driving License Expiry Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={profileData.licenseExpiryDate}
                    onChange={(e) => setProfileData(prev => ({ ...prev, licenseExpiryDate: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Theory Test Pass Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={profileData.theoryTestPassDate}
                    onChange={(e) => setProfileData(prev => ({ ...prev, theoryTestPassDate: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Theory Test Reference Number"
                    fullWidth
                    value={profileData.theoryTestPassReference}
                    onChange={(e) => setProfileData(prev => ({ ...prev, theoryTestPassReference: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="DVLA Share Code"
                    fullWidth
                    value={profileData.dvlaShareCode}
                    onChange={(e) => setProfileData(prev => ({ ...prev, dvlaShareCode: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="profile-transmission-label">Transmission Type</InputLabel>
                    <Select
                      labelId="profile-transmission-label"
                      id="profile-transmission"
                      value={profileData.transmissionType}
                      label="Transmission Type"
                      onChange={(e) => setProfileData(prev => ({ ...prev, transmissionType: e.target.value }))}
                    >
                      <MenuItem value="Manual">Manual</MenuItem>
                      <MenuItem value="Automatic">Automatic</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={profileData.eyesightTestPassed}
                        onChange={(e) => setProfileData(prev => ({ ...prev, eyesightTestPassed: e.target.checked }))}
                      />
                    }
                    label="Eyesight Test Passed (20m license plate reading)"
                  />
                </Grid>
 
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1.5 }}><Chip icon={<EmergencyIcon />} label="Emergency & Medical" size="small" /></Divider>
                </Grid>
 
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Emergency Contact Name"
                    fullWidth
                    value={profileData.emergencyContactName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, emergencyContactName: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Emergency Contact Phone"
                    fullWidth
                    value={profileData.emergencyContactPhone}
                    onChange={(e) => setProfileData(prev => ({ ...prev, emergencyContactPhone: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Medical Conditions (that may affect driving)"
                    fullWidth
                    multiline
                    rows={2}
                    value={profileData.medicalConditions}
                    onChange={(e) => setProfileData(prev => ({ ...prev, medicalConditions: e.target.value }))}
                  />
                </Grid>
 
                <Grid size={{ xs: 12 }} sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1 }}
                  >
                    {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        {/* Right Column: License Upload & Password Change */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Grid container spacing={3}>
            {/* Driving License Image Upload Card */}
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
                  <SchoolIcon color="primary" fontSize="large" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                    Driving License Images & Verification
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                  <Typography variant="body2" fontWeight="bold">Status:</Typography>
                  {profileData.licenseStatus === 'Approved' && <Chip label="Approved" color="success" size="small" />}
                  {profileData.licenseStatus === 'Pending' && <Chip label="Pending Approval" color="warning" size="small" />}
                  {profileData.licenseStatus === 'Rejected' && <Chip label="Rejected" color="error" size="small" />}
                  {profileData.licenseStatus === 'None' && <Chip label="Not Uploaded" color="default" size="small" />}
                </Box>

                {profileData.licenseStatus === 'Rejected' && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    <strong>Rejection Reason:</strong> {profileData.licenseRejectionReason}
                  </Alert>
                )}

                <Grid container spacing={2}>
                  {/* Front Side */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Card variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Front Side</Typography>
                      {profileData.licenseFrontImagePath ? (
                        <Box sx={{ position: 'relative', width: '100%', height: 110, bgcolor: '#f5f5f5', borderRadius: 1.5, overflow: 'hidden', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '1px solid #e0e0e0' }}
                             onClick={() => handleOpenImageLightbox(profileData.licenseFrontImagePath)}>
                          <img src={getImageUrl(profileData.licenseFrontImagePath)} alt="License Front" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </Box>
                      ) : (
                        <Box sx={{ width: '100%', height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa', borderRadius: 1.5, border: '1px dashed #ccc' }}>
                          <Typography variant="caption" color="textSecondary">Not Selected</Typography>
                        </Box>
                      )}
                      <Button variant="outlined" component="label" size="small" startIcon={<UploadIcon />} sx={{ mt: 1.5, borderRadius: 2 }}>
                        Upload
                        <input type="file" accept="image/*" hidden onChange={(e) => handleLicenseUpload(e, 'front')} />
                      </Button>
                    </Card>
                  </Grid>

                  {/* Back Side */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Card variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Back Side</Typography>
                      {profileData.licenseBackImagePath ? (
                        <Box sx={{ position: 'relative', width: '100%', height: 110, bgcolor: '#f5f5f5', borderRadius: 1.5, overflow: 'hidden', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '1px solid #e0e0e0' }}
                             onClick={() => handleOpenImageLightbox(profileData.licenseBackImagePath)}>
                          <img src={getImageUrl(profileData.licenseBackImagePath)} alt="License Back" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </Box>
                      ) : (
                        <Box sx={{ width: '100%', height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa', borderRadius: 1.5, border: '1px dashed #ccc' }}>
                          <Typography variant="caption" color="textSecondary">Not Selected</Typography>
                        </Box>
                      )}
                      <Button variant="outlined" component="label" size="small" startIcon={<UploadIcon />} sx={{ mt: 1.5, borderRadius: 2 }}>
                        Upload
                        <input type="file" accept="image/*" hidden onChange={(e) => handleLicenseUpload(e, 'back')} />
                      </Button>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Change Password Card */}
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                  <LockIcon color="primary" fontSize="large" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                    Change Password
                  </Typography>
                </Box>

                <form onSubmit={handlePasswordSubmit}>
                  <Box display="flex" flexDirection="column" gap={2.5}>
                    <TextField
                      label="Current Password"
                      type="password"
                      required
                      fullWidth
                      value={passData.currentPassword}
                      onChange={(e) => setPassData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    />
                    <TextField
                      label="New Password"
                      type="password"
                      required
                      fullWidth
                      value={passData.newPassword}
                      onChange={(e) => setPassData(prev => ({ ...prev, newPassword: e.target.value }))}
                      helperText="Must be at least 6 characters."
                    />
                    <TextField
                      label="Confirm New Password"
                      type="password"
                      required
                      fullWidth
                      value={passData.confirmPassword}
                      onChange={(e) => setPassData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isChangingPass}
                      sx={{ borderRadius: 2, fontWeight: 'bold', py: 1, mt: 1 }}
                    >
                      {isChangingPass ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
                    </Button>
                  </Box>
                </form>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Driving License Lightbox Dialog */}
      <Dialog
        open={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold' }}>
          <span>Review Driving License Document</span>
          <Box display="flex" gap={1}>
            <IconButton onClick={handleZoomIn} color="primary" title="Zoom In">
              <ZoomInIcon />
            </IconButton>
            <IconButton onClick={handleZoomOut} color="primary" title="Zoom Out">
              <ZoomOutIcon />
            </IconButton>
            <IconButton onClick={handleRotateRight} color="primary" title="Rotate">
              <RotateIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, bgcolor: '#fafafa', overflow: 'auto', p: 2 }}>
          <Box sx={{ 
            transition: 'transform 0.2s ease', 
            transform: `rotate(${rotation}deg) scale(${scale})`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '70vh',
            maxWidth: '100%'
          }}>
            <img 
              src={getImageUrl(lightboxImgUrl)} 
              alt="License Large View" 
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }} 
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={() => setIsLightboxOpen(false)} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMsg(null)} severity="success" sx={{ width: '100%', borderRadius: 2 }}>
          {successMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StudentProfile;
